import React from 'react';
import { Phone, Clock, Download, Play, 
  // AlertTriangle, Trash2, Languages, User, 
  Bot, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import { Call } from '../../types';
import { 
  ConversationSummary, ConversationDetails, 
  getConversations, downloadConversation, 
  formatDateTime,  getConversationDetails, 
  formatDuration
} from '../../services/api/calls/conversationsApi';

export default function CallHistory() {
  const { state, dispatch } = useAppContext();
  const { calls, patients } = state;
  const navigate = useNavigate();
  
  const [externalConversations, setExternalConversations] = React.useState<ConversationSummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showConversationModal, setShowConversationModal] = React.useState(false);
  const [selectedConversation, setSelectedConversation] = React.useState<ConversationDetails | null>(null);

  const fetchExternalConversations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getConversations(50, 0);
      setExternalConversations(response ? response.conversations : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };
  
  React.useEffect(() => {
    fetchExternalConversations();
  }, []);

  const replayCall = (call: Call) => {
    dispatch({ type: 'SET_ACTIVE_CALL', payload: call });
    navigate('/calls');
  };

  const downloadCallTranscript = (call: Call) => {
    const transcript = call.messages.map(msg => 
      `[${msg.timestamp.toLocaleTimeString()}] ${msg.speaker.toUpperCase()}: ${msg.content}`
    ).join('\n');
    
    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-transcript-${call.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const downloadExternalConversation = (conversation: ConversationSummary) => {
    downloadConversation(conversation.call_id, 'txt').catch(() => {
      // Fallback to manual download if API fails
      const content = `Call ID: ${conversation.call_id}
Start Time: ${formatDateTime(conversation.start_time)}
End Time: ${formatDateTime(conversation.end_time)}
Duration: ${formatDuration(conversation.duration_seconds)}
User: ${conversation.user_participant}
Assistant: ${conversation.assistant_participant}`;

      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation-${conversation.call_id}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const viewExternalConversation = async (callId: number) => {
    try {
      const details = await getConversationDetails(callId);
      setSelectedConversation(details);
      setShowConversationModal(true);
    } catch (err) {
      console.error('Failed to fetch conversation details:', err);
    }
  };

  const closeConversationModal = () => {
    setShowConversationModal(false);
    setSelectedConversation(null);
  };

  const getUrgencyColor = (level: Call['urgencyLevel']) => {
    switch (level) {
      case 'emergency': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 h-fit">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Call History</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={fetchExternalConversations}
            disabled={loading}
            className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 text-sm"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <span className="text-sm text-gray-500">
            {calls.length + externalConversations.length} total calls
          </span>
        </div>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">Error: {error}</p>
        </div>
      )}
      
      {calls.length === 0 && externalConversations.length === 0 ? (
        <div className="text-center py-12">
          <Phone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No calls yet</p>
          <p className="text-sm text-gray-400">Start a call to see history here</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {/* External Conversations */}
          {externalConversations.map((conversation) => (
            <div key={conversation.call_id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {conversation.user_participant} → {conversation.assistant_participant}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>external call</span>
                      <span>•</span>
                      <span>{formatDateTime(conversation.start_time)}</span>
                      <span>•</span>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{formatDuration(conversation.duration_seconds)}</span>
                      </div>
                      <span>•</span>
                      <div className="flex items-center space-x-1">
                        <Bot className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-600">AI</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => viewExternalConversation(+conversation.call_id)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                    title="View conversation"
                  >
                    <Play className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => downloadExternalConversation(conversation)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                    title="Download conversation"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Local Calls */}
          {calls.map((call) => {
            const patient = patients.find(p => p.id === call.patientId);
            return (
              <div key={call.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-3 h-3 rounded-full ${
                      call.status === 'ended' ? 'bg-green-500' : 
                      call.status === 'connected' ? 'bg-blue-500' : 'bg-red-500'
                    }`}></div>
                    
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {call.patientName} ({patient?.phone || 'Unknown'})
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(call.urgencyLevel)}`}>
                          {call.urgencyLevel}
                        </span>
                        <span>•</span>
                        <span>{call.startTime.toLocaleDateString()} {call.startTime.toLocaleTimeString()}</span>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{call.duration ? formatDuration(call.duration) : 'N/A'}</span>
                        </div>
                        <span>•</span>
                        <div className="flex items-center space-x-1">
                          <Bot className="w-4 h-4 text-blue-600" />
                          <span className="text-blue-600">AI</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => replayCall(call)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Replay call"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => downloadCallTranscript(call)}
                      className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                      title="Download transcript"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showConversationModal && selectedConversation && (
        <ConversationDetailsModal
          conversation={selectedConversation}
          onClose={closeConversationModal}
        />
      )}
    </div>
  );
}

// Conversation Details Modal Component
function ConversationDetailsModal({
  conversation,
  onClose
}: {
  conversation: ConversationDetails;
  onClose: () => void;
}) {
  const downloadFullTranscript = () => {
    downloadConversation(conversation.call_id, 'txt').catch(() => {
      // Fallback to manual download if API fails
      const transcript = conversation.conversation.map(msg => 
        `[${formatDateTime(msg.timestamp)}] ${msg.speaker.toUpperCase()}: ${msg.text}`
      ).join('\n');
      
      const fullContent = `Call ID: ${conversation.call_id}
Start Time: ${formatDateTime(conversation.start_time)}
End Time: ${formatDateTime(conversation.end_time)}
Duration: ${formatDuration(conversation.duration_seconds)}
User: ${conversation.user_participant}
Assistant: ${conversation.assistant_participant}
Created: ${formatDateTime(conversation.created_at)}
CONVERSATION TRANSCRIPT:
${transcript}`;
      
      const blob = new Blob([fullContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `full-conversation-${conversation.call_id}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl h-5/6 flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {conversation.user_participant} → {conversation.assistant_participant}
              </h3>
              <p className="text-sm text-gray-600">
                Duration: {formatDuration(conversation.duration_seconds)} • 
                Call ID: {conversation.call_id.substring(0, 8)}...
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={downloadFullTranscript}
              className="p-2 text-gray-400 hover:text-green-600 rounded-lg"
              title="Download Full Transcript"
            >
              <Download className="w-5 h-5" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Conversation Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="space-y-4">
            {conversation.conversation.map((message, index) => (
              <div key={index}>
                <div className={`flex ${message.speaker === 'assistant' ? 'justify-start' : 'justify-end'}`}>
                  <div
                    className={`max-w-xs px-4 py-3 rounded-lg ${
                      message.speaker === 'assistant'
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-blue-500 text-white'
                    }`}
                  >
                    <p>{message.text}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs opacity-70">
                        {formatDateTime(message.timestamp)}
                      </span>
                      <span className="text-xs opacity-70 capitalize">
                        {message.speaker}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Started:</span>
              <p className="text-gray-900">{formatDateTime(conversation.start_time)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Ended:</span>
              <p className="text-gray-900">{formatDateTime(conversation.end_time)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Duration:</span>
              <p className="text-gray-900">{formatDuration(conversation.duration_seconds)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Messages:</span>
              <p className="text-gray-900">{conversation.conversation.length}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}